import React, { useState, useEffect, useCallback } from 'react';
import { getAppointmentStats, getPendingSOAP, exportExcel, exportPDF } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3,
  Download,
  FileSpreadsheet,
  FileText,
  Calendar,
  TrendingUp,
  TrendingDown,
  Users,
  AlertCircle,
} from 'lucide-react';

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [pendingSOAP, setPendingSOAP] = useState([]);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    status: '',
  });
  const [isExporting, setIsExporting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const [statsRes, soapRes] = await Promise.all([
        getAppointmentStats(params),
        getPendingSOAP(),
      ]);
      
      // Laravel devuelve {data: {...}} o {data: [...]}, Axios extrae el data
      setStats(statsRes.data?.data || statsRes.data);
      const soapData = Array.isArray(soapRes.data?.data) ? soapRes.data.data : (Array.isArray(soapRes.data) ? soapRes.data : []);
      setPendingSOAP(soapData.filter(p => p.completed_appointments > 0 && (p.soap_count || 0) === 0));
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    }
  }, [filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;
      
      const response = format === 'excel'
        ? await exportExcel(params)
        : await exportPDF(params);
      
      const blob = new Blob([response.data], {
        type: format === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `citas.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setIsExporting(false);
    }
  };

  const statusLabels = {
    PENDIENTE: 'Pendiente',
    CONFIRMADA: 'Confirmada',
    CANCELADA: 'Cancelada',
    NO_SHOW: 'No se presentó',
    COMPLETADA: 'Completada',
    REQUIERE_REAGENDAR: 'Requiere reagendar',
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Reportes</h1>
        <p className="text-[#5D5C61] mt-1">Estadísticas y análisis de la agenda</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#8D99AE]" />
                Fecha inicio
              </Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="reports-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha fin</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="reports-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select
                value={filters.status || "ALL"}
                onValueChange={(value) => setFilters({ ...filters, status: value === "ALL" ? "" : value })}
              >
                <SelectTrigger data-testid="reports-status-select">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Exportar</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleExport('excel')}
                  disabled={isExporting}
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleExport('pdf')}
                  disabled={isExporting}
                  data-testid="export-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-100" data-testid="stats-total">
            <CardHeader className="pb-2">
              <CardDescription>Total de citas</CardDescription>
              <CardTitle className="text-3xl font-bold text-[#335C67]">
                {stats.total}
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card className="border-slate-100" data-testid="stats-completed">
            <CardHeader className="pb-2">
              <CardDescription>Completadas</CardDescription>
              <CardTitle className="text-3xl font-bold text-[#588157]">
                {stats.by_status?.COMPLETADA || 0}
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card className="border-slate-100" data-testid="stats-cancellation">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                Tasa de cancelación
                {stats.cancellation_rate > 20 ? (
                  <TrendingUp className="w-4 h-4 text-[#9E2A2B]" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-[#588157]" />
                )}
              </CardDescription>
              <CardTitle className="text-3xl font-bold" style={{
                color: stats.cancellation_rate > 20 ? '#9E2A2B' : '#588157'
              }}>
                {stats.cancellation_rate.toFixed(1)}%
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card className="border-slate-100" data-testid="stats-inasistencia">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                Tasa de inasistencia
                {stats.no_show_rate > 10 ? (
                  <TrendingUp className="w-4 h-4 text-[#9E2A2B]" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-[#588157]" />
                )}
              </CardDescription>
              <p className="text-xs text-[#5D5C61] mt-0.5">
                Porcentaje de citas marcadas como &quot;No asistió&quot; respecto al total en el periodo seleccionado.
              </p>
              <CardTitle className="text-3xl font-bold mt-1" style={{
                color: stats.no_show_rate > 10 ? '#9E2A2B' : '#588157'
              }}>
                {stats.no_show_rate.toFixed(1)}%
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Status Breakdown */}
      {stats && (
        <Card className="border-slate-100 mb-6">
          <CardHeader>
            <CardTitle className="text-xl font-['Manrope']">Desglose por estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(stats.by_status || {}).map(([status, count]) => (
                <div key={status} className="text-center p-4 bg-[#F4F1EA] rounded-lg">
                  <p className="text-2xl font-bold text-[#2B2D42]">{count}</p>
                  <p className="text-sm text-[#5D5C61]">{statusLabels[status] || status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending SOAP Notes */}
      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#E09F3E]" />
            <div>
              <CardTitle className="text-xl font-['Manrope']">
                Pacientes pendientes de Nota SOAP
              </CardTitle>
              <CardDescription>
                Pacientes con citas completadas que aún no tienen notas SOAP
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {pendingSOAP.length > 0 ? (
            <div className="space-y-3">
              {pendingSOAP.map((patient) => (
                <div
                  key={patient.patient_id}
                  className="flex items-center justify-between p-4 bg-[#F4F1EA] rounded-lg"
                  data-testid={`pending-soap-${patient.patient_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#335C67] flex items-center justify-center text-white font-semibold">
                      {patient.patient_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-[#2B2D42]">{patient.patient_name}</p>
                      <p className="text-sm text-[#5D5C61]">
                        {patient.completed_appointments} citas completadas
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-[#E09F3E] hover:bg-[#E09F3E]">
                    Nota pendiente
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-[#588157] mx-auto mb-3" />
              <p className="text-[#5D5C61]">Todos los pacientes están al día</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
